<template>
    <modal @close="$emit('close')" ref="modal" size="lg" :autoshow="true">
        <template v-slot:title>{{ displayAccounts.length > 1 ? 'Split Paychecks' : (mode == 'add' ? 'Add Direct Deposit' : 'Edit Direct Deposit') }}{{ step == 'summary' ? " - Summary" : "" }}</template>

        <!-- Split paychecks mode header -->
        <template v-if="step == 'account-details' && displayAccounts.length > 1" v-slot:subheading>
            <div class="dd-split-mode-toggle">
                <div class="d-flex align-items-center mb-1 dd-split-mode-heading">
                    <strong>How should each paycheck be split?</strong>
                    <more-info class="mt-0">
                        <p>Each paycheck can be split between multiple bank accounts, either by percentage or by amount.</p>
                        <p>When splitting by amount, you can reorder the accounts by clicking the up or down arrows. We'll pay the amounts in the order specified until the paycheck has been fully distributed to the last remainder account.</p>
                    </more-info>
                </div>
                <div class="d-flex align-items-end justify-content-between">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <a class="nav-link" href data-toggle="tab" role="tab" :class="{active: splitMode == 'percentage'}" @click.prevent="setSplitMode('percentage')"><span class="d-none d-md-inline">By </span>Percentage (%)</a>
                        </li>
                        <li class="nav-item">
                            <a class="nav-link" href data-toggle="tab" role="tab" :class="{active: splitMode == 'fixed'}" @click.prevent="setSplitMode('fixed')"><span class="d-none d-md-inline">By </span>Amount ($)</a>
                        </li>
                    </ul>
                    <button type="button" class="btn btn-outline-primary ml-2 mr-2" @click.prevent="addAccount()">+ Add <span class="d-none d-md-inline">Another </span>Account</button>
                </div>
            </div>
        </template>

        <slot>
            <form-errors :errors="errors['__all__']" />

            <div v-if="step == 'account-details'">
                <div v-for="(acct, idx) in displayAccounts" :key="acct._key" class="d-flex align-items-start mb-2">
                    <div v-if="displayAccounts.length > 1" class="dd-account-order-col flex-shrink-0">
                        <button v-if="splitMode == 'fixed' && canMoveAccountUp(acct)" type="button" class="dd-arrow-btn text-primary" @click.prevent="moveAccountUp(acct)" aria-label="Move account up">
                            <i class="far fa-fw fa-chevron-up"></i>
                        </button>
                        <div class="dd-account-number-circle">{{ idx + 1 }}</div>
                        <button v-if="splitMode == 'fixed' && canMoveAccountDown(acct)" type="button" class="dd-arrow-btn text-primary" @click.prevent="moveAccountDown(acct)" aria-label="Move account down">
                            <i class="far fa-fw fa-chevron-down"></i>
                        </button>
                    </div>
                    <div class="dd-account-details-form card flex-grow-1" :data-account-key="acct._key">
                    <div class="card-body px-2 pt-2 pb-0">
                        <div class="form-row">
                            <div class="col">
                                <form-errors :errors="errors[`accounts.${acct._key}.__all__`]"/>
                            </div>
                        </div>
                        <!-- Remainder card with non-AOS client gets a Direct Deposit / Paper Check selector
                             Hidden for existing accounts (acct.id) until the user clicks Edit Account, which clears the id. -->
                        <div v-if="!aosClient && acct.split_type == 'remainder' && !mandatoryDirectDeposit && !acct.id && displayAccounts.length > 1" class="form-row">
                            <div class="col">
                                <form-input v-model="acct.payment_method" noblank="true" type="select" label="Remainder Payment Method" :options="paymentMethodOptions"/>
                            </div>
                        </div>

                        <!-- Bank fields (hidden when remainder is set to Paper Check) -->
                        <template v-if="acct.payment_method != 'paper_check'">
                            <!-- Readonly bank name for existing accounts -->
                            <div v-if="acct.id && acct.bank_name" class="form-row">
                                <div class="col">
                                    <p>
                                        <span class="truncate-wrapper truncate-90 truncate-with-last4">
                                            <span class="truncate-content" :title="acct.bank_name || 'Bank'">
                                                <strong>{{ acct.bank_name || 'Bank' }}</strong>
                                            </span>
                                            <span class="truncate-suffix"><strong>({{ acct.account_number_redacted.slice(acct.account_number_redacted.length - 4) }})</strong></span>
                                        </span>
                                    </p>
                                </div>
                            </div>
                            <!-- Row: Routing Number | Account Type -->
                            <div class="form-row">
                                <div v-if="acct.id" class="col-md-6 col-12">
                                    <div class="mui-textfield">
                                        <span class="textfield">{{ acct.routing_number }}</span>
                                        <label>Routing Number</label>
                                    </div>
                                </div>
                                <div v-else class="col-md-6 col-12">
                                    <form-input
                                        v-model="acct.routing_number"
                                        :errors="errors[`accounts.${acct._key}.routing_number`]"
                                        type="integer-string"
                                        label="Routing Number"
                                        maxlength="9"
                                        extraclasses=""
                                        @update:model-value="onUpdateRoutingNumber(acct)"
                                        :helperHtml="bankNameHelperHtml(acct.bank_name)"
                                        :popoverHelperHtml="'<p>Your bank&rsquo;s Routing Number is the first set of numbers on the bottom of your check. It is 9 digits long.</p>'"
                                    />
                                </div>
                                <div class="col-md-6 col-12">
                                    <div v-if="acct.id" class="mui-textfield">
                                        <span class="textfield">{{ getDisplayAccountType(acct.account_type) }}</span>
                                        <label>Account Type</label>
                                    </div>
                                    <form-input v-else v-model="acct.account_type" :errors="errors[`accounts.${acct._key}.account_type`]" type="select" label="Account Type" :options="accountTypeOptions"/>
                                </div>
                            </div>
                            <!-- Row: Account Number | Confirm Account Number -->
                            <div class="form-row">
                                <div v-if="acct.id" class="col-md-6 col-12">
                                    <div class="mui-textfield">
                                        <span class="textfield">{{ acct.account_number_redacted }}</span>
                                        <label>Account Number</label>
                                    </div>
                                </div>
                                <template v-else>
                                    <div class="col-md-6 col-12">
                                        <form-input
                                            v-model="acct.account_number"
                                            :errors="errors[`accounts.${acct._key}.account_number`]"
                                            type="text"
                                            label="Account Number"
                                            maxlength="17"
                                            :popoverHelperHtml="'<p>Your Account Number is the second set of numbers on the bottom of your check. It can vary in length.</p>'"
                                        />
                                    </div>
                                    <div class="col-md-6 col-12">
                                        <form-input
                                            v-model="acct.confirm_account_number"
                                            :errors="errors[`accounts.${acct._key}.confirm_account_number`]"
                                            type="text"
                                            label="Confirm Account Number"
                                            maxlength="17"
                                        />
                                    </div>
                                </template>
                            </div>
                        </template>
                        <!-- Row: How much / percentage | Edit / Remove buttons -->
                        <div class="form-row align-items-center">
                            <div class="col-md-6 col-12">
                                <div class="label-like">
                                    {{ displayAccounts.length == 1 || splitMode == 'fixed' ? 'How much of each paycheck?' : 'What percentage of each paycheck?' }}
                                </div>
                                <div v-if="acct.split_type == 'remainder'" class="mui-textfield pt-0">
                                    <span class="textfield">{{ remainderAmountDisplay }}</span>
                                </div>
                                <div v-else class="row no-gutters">
                                    <div :class="acct.split_type == 'percentage' ? 'col-4' : 'col-6'">
                                        <form-input v-if="acct.split_type == 'fixed'" v-model="acct.amount" :errors="errors[`accounts.${acct._key}.amount`]" type="currency" prefix="$" />
                                        <form-input v-if="acct.split_type == 'percentage'" v-model="acct.percentage" :errors="errors[`accounts.${acct._key}.percentage`] || percentageOverflowError" type="integer" maxlength="2" postfix="%" />
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-6 col-12 text-md-right dd-account-actions mb-2 mb-md-0">
                                <button v-if="acct.id && !(acct.split_type == 'remainder' && acct.payment_method == 'paper_check')" type="button" class="btn btn-outline-primary btn-sm mr-1" @click.prevent="clearAccount(acct)">
                                    <i class="far fa-fw fa-pen mr-1"></i>Edit Account
                                </button>
                                <button v-if="acct.split_type != 'remainder'" type="button" class="btn btn-outline-primary btn-sm" @click.prevent="removeAccount(acct)">
                                    <i class="far fa-fw fa-trash mr-1"></i>Remove Account
                                </button>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>

            </div>

            <template v-if="step == 'summary'">
                <div v-if="turningOffDirectDeposit">
                    <p v-if="directDepositAccounts && directDepositAccounts.length > 0">
                        Your bank account{{ directDepositAccounts.length > 1 ? 's' : '' }} will be removed
                        <template v-if="!mandatoryDirectDeposit">and you will receive <strong>100% of each paycheck in a paper check</strong></template>
                        <template v-else-if="mandatoryDirectDeposit && !approvalView">and you will receive <strong>100% of each paycheck on a paycard</strong></template>
                    </p>
                </div>
                <template v-else>
                    <p v-if="isPaperCheckOnly">
                        You will receive <strong>100% of each paycheck in a paper check</strong>.
                    </p>
                    <template v-else>
                        <div v-for="(acct, idx) in displayAccounts" :key="acct._key" class="d-flex align-items-start mb-2">
                            <div v-if="displayAccounts.length > 1" class="dd-account-number-circle flex-shrink-0">{{ idx + 1 }}</div>
                            <div class="flex-grow-1">
                                <direct-deposit-account-card
                                    :account="acct"
                                    :paperCheck="acct.split_type == 'remainder' && acct.payment_method == 'paper_check'"
                                    :numAccounts="displayAccounts.length"
                                    :approvalView="approvalView"
                                    :url="url"
                                >
                                </direct-deposit-account-card>
                            </div>
                        </div>
                    </template>
                </template>
            </template>
        </slot>

        <template v-slot:footer>
            <div v-if="step == 'account-details' && displayAccounts.length == 1" class="form-row mb-2">
                <div class="col text-center">
                    <button type="button" class="btn btn-link" @click.prevent="addAccount()">
                        <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" class="mr-1" aria-hidden="true">
                            <path d="M0 0h24v24H0z" fill="none" />
                            <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
                                <path d="M16 3h5v5M8 3H3v5" />
                                <path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3m12 6l6-6" />
                            </g>
                        </svg>Split Paychecks
                    </button>
                </div>
            </div>
            <div v-if="step == 'account-details' && showTurnOffDirectDepositLink" class="form-row mb-2">
                <div class="col text-center dd-turn-off-link">
                    <a href @click.prevent="turnOffDirectDeposit()">
                        <template v-if="!mandatoryDirectDeposit">Turn off Direct Deposit (receive a paper check instead)</template>
                        <template v-else-if="mandatoryDirectDeposit && !approvalView">Turn Off Direct Deposit (receive a paycard instead)</template>
                        <template v-else-if="mandatoryDirectDeposit && approvalView && canOverrideMandatoryDirectDeposit">Turn off Direct Deposit</template>
                    </a>
                </div>
            </div>
            <div class="form-row">
                <div class="col">
                    <button v-if="step == 'account-details'" type="button" class="btn btn-block btn-outline-primary" @click.prevent="close()">Cancel</button>
                    <button v-else type="button" class="btn btn-block btn-outline-primary" @click.prevent="prevStep()">Back</button>
                </div>
                <div class="col">
                    <button v-if="step == 'summary'" type="submit" class="btn btn-block btn-primary" @click.prevent="onSubmit()">Save</button>
                    <button v-else class="btn btn-block btn-primary" @click.prevent="nextStep()">Next</button>
                </div>
            </div>
        </template>
    </modal>
</template>

<script>
import api from '@/api'
import DirectDepositAccountCard from '@/components/DirectDepositAccountCard'
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'

export default {
    mixins: [FormMixin, ModalMixin],
    components: {DirectDepositAccountCard, },
    props: {
        directDepositAccounts: null,
        url: null,
        mode: null,
        approvalView: null,
        essView: null,
        mandatoryDirectDeposit: null,
        canOverrideMandatoryDirectDeposit: null,
        aosClient: {default: true},
    },
    emits: ['close', 'updated'],
    computed: {
        accountTypeOptions() {
            return [
                {text: 'Checking', value: 'CHECKING'},
                {text: 'Savings', value: 'SAVINGS'},
            ]
        },
        paymentMethodOptions() {
            return [
                {text: 'Direct Deposit', value: 'direct_deposit'},
                {text: 'Paper Check', value: 'paper_check'},
            ]
        },
        displayAccounts() {
            // Remainder is always last
            return [...this.formData.accounts].sort((a, b) => {
                return (a.split_type == 'remainder' ? 1 : 0) - (b.split_type == 'remainder' ? 1 : 0)
            })
        },
        submittableAccounts() {
            // Same order as the UI; just drops the remainder card if user picked paper-check
            // (which means there's no remainder direct-deposit account to submit).
            return this.displayAccounts.filter(a => !(a.split_type == 'remainder' && a.payment_method == 'paper_check'))
        },
        isPaperCheckOnly() {
            // The user opted into paper-check on the remainder and has no other accounts —
            // the whole paycheck is paid by paper check.
            return this.displayAccounts.length == 1
                && this.displayAccounts[0].split_type == 'remainder'
                && this.displayAccounts[0].payment_method == 'paper_check'
        },
        percentageTotal() {
            // Sum of the entered percentages across non-remainder accounts
            let total = 0
            for (const a of this.formData.accounts) {
                if (a.split_type == 'percentage') {
                    const p = parseInt(a.percentage, 10)
                    if (!isNaN(p)) total += p
                }
            }
            return total
        },
        remainderPercentage() {
            return Math.max(0, 100 - this.percentageTotal)
        },
        percentageOverflowError() {
            return this.percentageTotal > 100 ? ['Percentages must total 100% or less'] : null
        },
        remainderAmountDisplay() {
            if (this.displayAccounts.length == 1) {
                return 'Entire paycheck'
            }
            if (this.splitMode == 'percentage') {
                return `Remainder (${this.remainderPercentage}%)`
            }
            return 'Remainder'
        },
        showTurnOffDirectDepositLink() {
            if (this.mode != 'edit') {
                return false
            }
            if (!this.directDepositAccounts || this.directDepositAccounts.length == 0) {
                return false
            }
            if (this.isPaperCheckOnly) {
                return false
            }
            if (this.essView && this.mandatoryDirectDeposit) {
                return false
            }
            if (this.approvalView && this.mandatoryDirectDeposit && !this.canOverrideMandatoryDirectDeposit) {
                return false
            }
            return true
        },
    },
    data() {
        const formData = this.makeFormData()
        // determine initial split mode based on the first non-remainder account, or default to percentage if no accounts or only a remainder account
        const firstPortion = formData.accounts.find(a => a.split_type != 'remainder')
        const splitMode = firstPortion ? firstPortion.split_type : 'percentage'
        for (const a of formData.accounts) {
            if (a.split_type != 'remainder') {
                a.split_type = splitMode
            }
        }
        return {
            formRules: {},
            formLabels: {},
            formData,
            step: 'account-details',
            errors: {},
            turningOffDirectDeposit: false,
            splitMode,
        }
    },
    methods: {
        makeAccountKey(existingId) {
            // Stable v-for key for an account form object. Existing accounts (with a backend id)
            // key off that id; new (unsaved) accounts get a random suffix.
            if (existingId) {
                return `db_${existingId}`
            }
            return `new_${Math.random().toString(36).slice(2, 11)}`
        },
        makeFormData() {
            // Build an array of account form objects from existing directDepositAccounts.
            // The remainder card is always present. For non-AOS clients, if there's no existing remainder, synthesize one with payment_method=paper_check.
            const accounts = []
            let hasRemainder = false
            const existing = this.directDepositAccounts || []
            for (const a of existing) {
                if (a.split_type == 'remainder') {
                    accounts.push(this.makeAccountFormData(a, 'direct_deposit'))
                    hasRemainder = true
                } else {
                    accounts.push(this.makeAccountFormData(a))
                }
            }
            if (!hasRemainder) {
                // Add a remainder card if one doesn't already exist. For non-AOS clients, it could be a synthetic paper-check remainder
                const isImplicitPaperCheck = !this.aosClient && existing.length > 0
                accounts.push(this.makeRemainderFormData(isImplicitPaperCheck ? 'paper_check' : 'direct_deposit'))
            }
            return {accounts}
        },
        makeAccountFormData(existingAccount, paymentMethod) {
            const isExisting = !!existingAccount
            const ret = {
                _key: this.makeAccountKey(existingAccount && existingAccount.id),
                routing_number: isExisting ? existingAccount.routing_number : '',
                account_number: isExisting ? (existingAccount.account_number || '') : '',
                confirm_account_number: isExisting ? (existingAccount.account_number || '') : '',
                account_number_redacted: isExisting ? (existingAccount.account_number_redacted || '') : '',
                account_type: isExisting ? (existingAccount.account_type || '') : '',
                bank_name: isExisting ? existingAccount.bank_name : '',
                id: isExisting ? (existingAccount.id || '') : '',
                split_type: isExisting ? existingAccount.split_type : 'percentage',
                amount: isExisting ? existingAccount.amount : '',
                percentage: isExisting ? existingAccount.percentage : '',
                payment_method: paymentMethod || 'direct_deposit',
            }
            return ret
        },
        makeRemainderFormData(paymentMethod) {
            const ret = this.makeAccountFormData(null)
            ret.split_type = 'remainder'
            ret.amount = ''
            ret.percentage = ''
            ret.payment_method = paymentMethod || 'direct_deposit'
            return ret
        },
        addAccount() {
            const newAcct = this.makeAccountFormData(null)
            newAcct.split_type = this.splitMode
            this.formData.accounts.unshift(newAcct)
            this.$nextTick(() => {
                const el = document.querySelector(`[data-account-key="${newAcct._key}"]`)
                if (el) {
                    el.scrollIntoView({behavior: 'smooth', block: 'nearest'})
                }
            })
        },
        setSplitMode(mode) {
            if (this.splitMode == mode) return
            this.splitMode = mode
            for (const a of this.formData.accounts) {
                if (a.split_type == 'remainder') continue
                a.split_type = mode
                if (mode == 'percentage') {
                    a.amount = ''
                    delete this.errors[`accounts.${a._key}.amount`]
                } else {
                    a.percentage = ''
                    delete this.errors[`accounts.${a._key}.percentage`]
                }
            }
        },
        removeAccount(account) {
            if (account.split_type == 'remainder') {
                return
            }
            this.formData.accounts = this.formData.accounts.filter(a => a._key != account._key)
            // Clear any errors that referenced this account
            const prefix = `accounts.${account._key}.`
            for (const key of Object.keys(this.errors)) {
                if (key.startsWith(prefix)) {
                    delete this.errors[key]
                }
            }
            // Removing all portion accounts returns the user to the simplified single-account view.
            // The Payment Method dropdown isn't shown in that view, so reset the remainder to direct deposit.
            if (this.formData.accounts.length == 1) {
                const remainder = this.formData.accounts[0]
                if (remainder.split_type == 'remainder' && !remainder.id) {
                    remainder.payment_method = 'direct_deposit'
                }
            }
        },
        canMoveAccountUp(account) {
            // True if there's another account of the same split_type above this one in the array.
            // (Remainder cards have no siblings, so this is always false for them.)
            const accounts = this.formData.accounts
            const idx = accounts.findIndex(a => a._key == account._key)
            for (let i = idx - 1; i >= 0; i--) {
                if (accounts[i].split_type == account.split_type) return true
            }
            return false
        },
        canMoveAccountDown(account) {
            const accounts = this.formData.accounts
            const idx = accounts.findIndex(a => a._key == account._key)
            for (let i = idx + 1; i < accounts.length; i++) {
                if (accounts[i].split_type == account.split_type) return true
            }
            return false
        },
        moveAccountUp(account) {
            const accounts = this.formData.accounts
            const aIdx = accounts.findIndex(a => a._key == account._key)
            if (aIdx === -1) return
            let bIdx = -1
            for (let i = aIdx - 1; i >= 0; i--) {
                if (accounts[i].split_type == account.split_type) {
                    bIdx = i
                    break
                }
            }
            if (bIdx === -1) return
            const a = accounts[aIdx]
            const b = accounts[bIdx]
            accounts.splice(aIdx, 1, b)
            accounts.splice(bIdx, 1, a)
        },
        moveAccountDown(account) {
            const accounts = this.formData.accounts
            const aIdx = accounts.findIndex(a => a._key == account._key)
            if (aIdx === -1) return
            let bIdx = -1
            for (let i = aIdx + 1; i < accounts.length; i++) {
                if (accounts[i].split_type == account.split_type) {
                    bIdx = i
                    break
                }
            }
            if (bIdx === -1) return
            const a = accounts[aIdx]
            const b = accounts[bIdx]
            accounts.splice(aIdx, 1, b)
            accounts.splice(bIdx, 1, a)
        },
        turnOffDirectDeposit() {
            this.turningOffDirectDeposit = true
            this.step = 'summary'
        },
        getFormData() {
            // Expose accounts as an object keyed by _key so FormMixin's path-based validator can resolve rules like `accounts.<_key>.routing_number`.
            const accountsByKey = {}
            for (const a of this.formData.accounts) {
                accountsByKey[a._key] = a
            }
            return {
                ...this.formData,
                accounts: accountsByKey,
            }
        },
        getSubmitFormData() {
            const submitFormData = {accounts: []}
            for (const a of this.submittableAccounts) {
                submitFormData.accounts.push({
                    account_number: a.account_number,
                    routing_number: a.routing_number,
                    account_type: a.account_type,
                    split_type: a.split_type,
                    amount: a.amount,
                    percentage: a.percentage,
                    bank_name: a.bank_name,
                    id: a.id,
                })
            }
            return submitFormData
        },
        setErrors(errors) {
            this.errors['__all__'] = errors.__all__
            if (errors.accounts && errors.accounts.length) {
                // The submit payload is built from submittableAccounts in display order,
                // so per-account errors come back indexed in that same order.
                for (const [idx, accountErrors] of errors.accounts.entries()) {
                    const acct = this.submittableAccounts[idx]
                    if (!acct) continue
                    for (const [errorKey, errorMessages] of Object.entries(accountErrors)) {
                        this.errors[`accounts.${acct._key}.${errorKey}`] = errorMessages
                    }
                }
            }
        },
        reset() {
            this.formData = this.makeFormData()
            this.turningOffDirectDeposit = false
            this.resetErrors()
        },
        resetErrors() {
            this.errors = {}
            this.formErrors = []
        },
        nextStep() {
            if (this.step == 'account-details') {
                if (!this.validateAccountDetails()) {
                    return
                }
                this.turningOffDirectDeposit = false
                this.step = 'summary'
            }
        },
        prevStep() {
            if (this.step == 'summary') {
                this.turningOffDirectDeposit = false
                this.step = 'account-details'
            }
        },
        onUpdateRoutingNumber(account) {
            if (!account['routing_number']) {
                account.bank_name = ''
                return
            }
            if (account['routing_number'].length != 9) {
                account.bank_name = ''
                return
            }
            api.post('/validate/brn', {'routing_number': account['routing_number']}).then((resp) => {
                delete this.errors[`accounts.${account._key}.routing_number`]
                account.bank_name = resp['bank_name']
            }).catch((errors) => {
                this.errors[`accounts.${account._key}.routing_number`] = errors['routing_number']
                account.bank_name = ''
            })
        },
        clearAccount(account) {
            // Replace the existing account fields with blanks while preserving _key, split_type,
            // and payment_method (so the card stays in place).
            account.routing_number = ''
            account.account_number = ''
            account.confirm_account_number = ''
            account.account_number_redacted = ''
            account.account_type = ''
            account.bank_name = ''
            account.id = ''
        },
        validateAccountDetails() {
            // If the user removed every non-remainder account AND set the remainder to paper-check,
            // submittableAccounts will be empty — that's effectively turning off direct deposit, allow it.
            if (this.submittableAccounts.length == 0) {
                return true
            }

            // Build the per-account form rules for validation via FormMixin
            const formRules = {}
            const formLabels = {}
            for (const a of this.submittableAccounts) {
                const key = a._key
                const r = (suffix) => `accounts.${key}.${suffix}`

                formRules[r('routing_number')] = {presence: {allowEmpty: false}, aba_routing_number: true}
                // remove validation on account_number if we're using an existing account (and therefore account_number is redacted)
                formRules[r('account_number')] = {presence: {allowEmpty: !!a.id}}
                formRules[r('confirm_account_number')] = {presence: {allowEmpty: !!a.id}}
                formRules[r('account_type')] = {presence: {allowEmpty: false}}

                formLabels[r('routing_number')] = 'Routing Number'
                formLabels[r('account_number')] = 'Account Number'
                formLabels[r('confirm_account_number')] = 'Account Number'
                formLabels[r('account_type')] = 'Account Type'

                if (a.split_type != 'remainder') {
                    formRules[r('split_type')] = {presence: {allowEmpty: false}}
                    formLabels[r('split_type')] = 'Split Type'
                    if (a.split_type == 'percentage') {
                        formRules[r('percentage')] = {presence: {allowEmpty: false}, numericality: {greaterThan: 0}}
                        formLabels[r('percentage')] = 'Percentage'
                    } else if (a.split_type == 'fixed') {
                        formRules[r('amount')] = {presence: {allowEmpty: false}, numericality: {greaterThan: 0}}
                        formLabels[r('amount')] = 'Amount'
                    }
                }

                // early-return if we know from the results of onUpdateRoutingNumber that the routing number is not valid
                // NB: this doesn't validate the routing number for an existing account (but an error *will* eventually be returned on the final form submit if that is invalid)
                if (this.errors[r('routing_number')] && !a.id) {
                    return false
                }

                // Make sure account numbers match
                if (!a.id && a.account_number != a.confirm_account_number) {
                    this.errors[r('confirm_account_number')] = ['Account Number must match']
                    return false
                }
            }

            this.formRules = formRules
            this.formLabels = formLabels

            // Make sure percentages don't exceed 100%
            if (this.splitMode == 'percentage' && this.percentageTotal > 100) {
                this.errors['__all__'] = ['Direct deposit percentages cannot total more than 100%.']
                return false
            }

            return this.validate()
        },
        onSubmit() {
            this.$store.dispatch('START_LOADING')
            const data = this.getSubmitFormData()
            const isDelete = this.turningOffDirectDeposit || data.accounts.length == 0

            if (isDelete) {
                api.del(this.url, data).then((resp) => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$emit('updated', resp)
                    this.close()
                }).catch((errors) => {
                    this.setErrors(errors)
                    this.$store.dispatch('STOP_LOADING')
                })
            } else {
                api.post(this.url, data).then((resp) => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$emit('updated', resp)
                    this.close()
                }).catch((errors) => {
                    this.setErrors(errors)
                    this.step = 'account-details'
                    this.$store.dispatch('STOP_LOADING')
                })
            }
        },
        bankNameHelperHtml(bankName) {
            if (!bankName) {
                return ''
            }
            return '<i class="fas fa-fw fa-check mr-hf text-primary" aria-hidden="true"></i> ' + bankName
        },
        getDisplayAccountType(accountType) {
            if (accountType == 'CHECKING' || accountType == 'checking') {
                return 'Checking'
            } else if (accountType == 'SAVINGS' || accountType == 'savings') {
                return 'Savings'
            } else {
                return accountType
            }
        }
    },
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/globals";

.dd-account-number-circle {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: #e9ecef;
    color: #495057;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.875rem;
    margin-top: 0.75rem;
    margin-right: 0.5rem;
}
.dd-account-order-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;
    margin-right: 0.5rem;
}
.dd-account-order-col .dd-account-number-circle {
    // In the account-details step the up-arrow above provides the top spacing,
    // and the order column itself owns the right gutter.
    margin-top: 0;
    margin-right: 0;
}
.dd-arrow-btn {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1px solid;
    border-color: currentColor;
    background: transparent;
    padding: 0;
    line-height: 1;
    font-size: 0.75rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.15s ease-in-out, color 0.15s ease-in-out, border-color 0.15s ease-in-out;
}
.dd-arrow-btn:hover {
    background-color: $brand-orange;
    border-color: $brand-orange;
    color: #fff !important;
}

// Tighten the controls on mobile
@media (max-width: 575px) {
    .dd-split-mode-toggle {
        .dd-split-mode-heading {
            font-size: 0.85rem;
        }
        .nav-tabs .nav-link {
            font-size: 0.8rem;
            padding: 0.25rem 0.5rem;
        }
        .btn {
            font-size: 0.8rem;
            padding: 0.25rem 0.5rem;
            white-space: nowrap;
        }
    }
    .dd-account-actions .btn {
        font-size: 0.75rem;
        padding: 0.25rem 0.5rem;
    }
    .dd-turn-off-link {
        font-size: 0.75rem;
        white-space: nowrap;
    }
    // Slightly larger gutter to the form card on mobile.
    .dd-account-order-col {
        margin-right: 0.4rem;
        gap: 0.25rem;
    }
    .dd-account-number-circle {
        width: 24px;
        height: 24px;
        font-size: 0.85rem;
    }
    .dd-arrow-btn {
        width: 24px;
        height: 24px;
        font-size: 0.7rem;
    }
}
</style>
